// src/pages/Plan/ManualContribution.jsx - PRODUCTION MODE
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { planService } from '../../services/planService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './ManualContribution.css';

const ManualContribution = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, getCurrencySymbol, currency } = useCurrency();
  
  const [plan, setPlan] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    fetchData();
  }, [planId, currency]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch plan details
      const planData = await planService.getPlanDetail(planId);
      setPlan(planData);

      // Fetch wallet balance
      const walletResponse = await fetch(`${API_BASE_URL}/users/wallet/balance/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (walletResponse.ok) {
        const walletData = await walletResponse.json();
        setWalletBalance(walletData.balance);
      }

      // Fetch exchange rate
      const rateResponse = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (rateResponse.ok) {
        const rateData = await rateResponse.json();
        setExchangeRate(rateData);
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  // Trigger custom event to refresh alerts
  const triggerAlertRefresh = () => {
    const event = new CustomEvent('refreshAlerts');
    window.dispatchEvent(event);
  };

  const handleConfirm = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const numAmount = parseFloat(amount);
    
    // Validate minimum and maximum (in display currency)
    const minAmount = currency === 'USD' ? 5000 * parseFloat(exchangeRate?.ngn_to_usd || 0.0012) : 5000;
    const maxAmount = currency === 'USD' ? 100000000 * parseFloat(exchangeRate?.ngn_to_usd || 0.0012) : 100000000;

    if (numAmount < minAmount) {
      setError(`Minimum contribution is ${formatCurrency(minAmount)}`);
      return;
    }

    if (numAmount > maxAmount) {
      setError(`Maximum contribution is ${formatCurrency(maxAmount)}`);
      return;
    }

    if (numAmount > walletBalance) {
      setError(`Insufficient funds. You have ${formatCurrency(walletBalance)} available`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Convert to NGN if user is in USD
      let amountToSend = numAmount;
      if (currency === 'USD' && exchangeRate) {
        amountToSend = numAmount * parseFloat(exchangeRate.usd_to_ngn);
      }

      const result = await planService.executeManualContribution(planId, amountToSend);
      
      console.log('✅ Contribution result:', result);
      
      // Trigger alert refresh to update notification list
      triggerAlertRefresh();
      
      // Show success message
      alert(result.message);
      
      // Navigate back to dashboard
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Error executing contribution:', err);
      setError(err.message || 'Failed to process contribution');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIgnore = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const result = await planService.ignoreReminder(planId);
      
      console.log('✅ Ignore result:', result);
      
      // Trigger alert refresh to update notification list
      triggerAlertRefresh();
      
      alert('Reminder ignored. You will be reminded again in 4 weeks');
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Error ignoring reminder:', err);
      setError(err.message || 'Failed to ignore reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="manual-contribution-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading plan details...</p>
        </div>
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="manual-contribution-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currencySymbol = getCurrencySymbol();

  return (
    <div className="manual-contribution-page">
      <div className="contribution-container">
        <div className="contribution-header">
          <h1>💰 Contribute to Your Plan</h1>
          <button onClick={handleCancel} className="close-button">×</button>
        </div>

        {/* Plan Info Card */}
        <div className="plan-info-card">
          <h2>{plan.plan_name}</h2>
          <div className="plan-stats">
            <div className="stat">
              <span className="label">Current Balance</span>
              <span className="value">{formatCurrency(parseFloat(plan.current_balance))}</span>
            </div>
            <div className="stat">
              <span className="label">Total Contributions</span>
              <span className="value">{plan.contribution_count}</span>
            </div>
            <div className="stat">
              <span className="label">Total Reminders</span>
              <span className="value">{plan.reminder_count}</span>
            </div>
          </div>
        </div>

        {/* Wallet Balance */}
        <div className="wallet-info">
          <span className="wallet-label">💳 Available Balance:</span>
          <span className="wallet-amount">{formatCurrency(walletBalance)}</span>
        </div>

        {/* Contribution Form */}
        <div className="contribution-form">
          <h3>How much would you like to add?</h3>
          
          <div className="amount-input-container">
            <span className="currency-symbol">{currencySymbol}</span>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
              disabled={submitting}
              className="amount-input"
            />
          </div>

          <div className="amount-limits">
            <p>Minimum: {formatCurrency(currency === 'USD' ? 5000 * parseFloat(exchangeRate?.ngn_to_usd || 0.0012) : 5000)}</p>
            <p>Maximum: {formatCurrency(currency === 'USD' ? 100000000 * parseFloat(exchangeRate?.ngn_to_usd || 0.0012) : 100000000)}</p>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              onClick={handleConfirm}
              disabled={submitting || !amount}
              className="confirm-button"
            >
              {submitting ? 'Processing...' : 'Confirm Contribution'}
            </button>
            
            <button
              onClick={handleIgnore}
              disabled={submitting}
              className="ignore-button"
            >
              {submitting ? 'Processing...' : 'Ignore & Remind Me Later'}
            </button>
            
            <button
              onClick={handleCancel}
              disabled={submitting}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-text">
            <strong>Reminder Schedule:</strong> If you ignore this reminder, you'll receive another 
            one in 4 weeks.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualContribution;